import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  X, Ticket, Ruler, Image as ImageIcon, MessageSquare, Plus, Trash2, Loader2, FileText, Download, AlertTriangle, Sparkles, Camera, History, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STATUS, STATUS_OPTIONS, TICKET_STATUS_OPTIONS, DEFECT_STATUS_OPTIONS, TICKET_CATEGORIES, PRIORITIES, GEWERKE } from "@/lib/constants";
import { useStammdaten } from "@/lib/stammdaten";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PendingPhotoBadge from "@/components/PendingPhotoBadge";
import PendingItemBadge from "@/components/PendingItemBadge";
import PositionNotesTab from "@/components/PositionNotes";
import { Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import MediaUploader from "@/components/MediaUploader";
import MediaGrid from "@/components/MediaGrid";
import { QuantityForm, QuantityRow, EMPTY_QTY } from "@/components/QuantitiesPanel";
import { downloadFile } from "@/components/DocumentsPanel";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";

const helpTopic = (tab, role) => {
  if (tab === "maengel") { return role === "haustechnik" ? "loeschanfrage" : "mangel"; }
  if (tab === "tickets") { return "mangel"; }
  return "position";
};

const EMPTY_TICKET = { title: "", category: "mangel", description: "", status: "offen", priority: "normal", responsible: "", due_date: "", measures: "" };
const EMPTY_DEFECT = { bezeichnung: "", description: "", gewerk: "", location: "", company: "", employee: "", priority: "normal", due_date: "", status: "offen", cause: "", proposed_measure: "", actual_measure: "" };

function BadgeScaleControl({ position, setPosition, updatePosition }) {
  const timer = useRef(null);
  const v = position.badge_scale || 1;
  const change = (e) => {
    const n = parseFloat(e.target.value);
    setPosition((p) => ({ ...p, badge_scale: n }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => updatePosition({ badge_scale: n }).catch(() => toast.error("Schildgröße konnte nicht gespeichert werden")), 350);
  };
  return (
    <div className="flex items-center gap-2 mt-2" data-testid="position-badge-scale">
      <span className="text-xs text-slate-500 shrink-0">Schildgröße dieser Position</span>
      <input type="range" min="0.3" max="1.2" step="0.05" value={v} onChange={change}
        className="flex-1 accent-primary cursor-pointer" data-testid="position-badge-scale-slider" />
      <span className="text-xs font-mono-tech text-slate-600 w-10 text-right" data-testid="position-badge-scale-value">{Math.round(v * 100)}%</span>
    </div>
  );
}

export default function PositionDrawer({ positionId, onClose, onChanged, initialTab }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(initialTab || "tickets");
  const [position, setPosition] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [defects, setDefects] = useState([]);

  const reloadAll = useCallback(async () => {
    if (!positionId) return;
    const results = await Promise.allSettled([
      api.get(`/positions/${positionId}`),
      api.get(`/positions/${positionId}/tickets`),
      api.get(`/positions/${positionId}/quantities`),
      api.get(`/positions/${positionId}/files`),
      api.get(`/positions/${positionId}/comments`),
      api.get(`/positions/${positionId}/defects`),
    ]);
    const val = (i, d) => (results[i].status === "fulfilled" ? results[i].value.data : d);
    const pos = val(0, null);
    if (pos) setPosition(pos);
    setTickets(val(1, [])); setQuantities(val(2, [])); setFiles(val(3, [])); setComments(val(4, [])); setDefects(val(5, []));
  }, [positionId]);

  useEffect(() => { reloadAll(); }, [reloadAll]);
  // Reload after offline sync so temp records (op_...) are replaced by the real ones incl. their photos.
  useEffect(() => {
    let t = null;
    const onSync = () => { clearTimeout(t); t = setTimeout(reloadAll, 800); };
    window.addEventListener("offline-queued", onSync);
    return () => { clearTimeout(t); window.removeEventListener("offline-queued", onSync); };
  }, [reloadAll]);

  const notify = () => { reloadAll(); onChanged && onChanged(); };

  const updatePosition = async (patch) => {
    const { data } = await api.put(`/positions/${positionId}`, patch);
    setPosition(data); onChanged && onChanged();
  };

  const deletePosition = async () => {
    try {
      await api.delete(`/positions/${positionId}`);
      toast.success("Markierung gelöscht");
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!position)
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
        <div className="relative w-full max-w-xl bg-white h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </div>
    );

  const stt = STATUS[position.status] || STATUS.offen;
  const photos = files.filter((f) => f.category === "photo" && !f.defect_id && !f.quantity_id);
  const docs = files.filter((f) => f.category === "document");

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="position-drawer">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 justify-between w-full sm:w-auto order-first sm:order-last">
            <Select value={position.status} onValueChange={(v) => updatePosition({ status: v })}>
              <SelectTrigger className="w-44 h-9" data-testid="position-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="delete-position-btn" title="Markierung löschen" className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 className="h-5 w-5" /></button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Markierung löschen?</AlertDialogTitle><AlertDialogDescription>Diese Position und alle zugehörigen Tickets, Mängel, Fotos, Mengen und Kommentare werden unwiderruflich gelöscht.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={deletePosition} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-position-btn">Löschen</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Link to={`/hilfe?thema=${helpTopic(tab, user?.role)}`} data-testid="drawer-help-link" title="Hilfe zu diesem Bereich" className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary"><HelpCircle className="h-5 w-5" /></Link>
              <button onClick={onClose} data-testid="close-drawer-btn" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
            </div>
          </div>
          <div className="flex-1 w-full">
            <div className="flex items-center gap-2">
              <span className="font-mono-tech font-bold text-primary bg-primary/10 px-2 py-0.5 rounded text-sm">{position.number}</span>
              <Badge variant="outline" className={`${stt.bg} ${stt.text} ${stt.border}`}><span className="h-1.5 w-1.5 rounded-full mr-1" style={{ background: stt.dot }} />{stt.label}</Badge>
            </div>
            <Input value={position.title || ""} placeholder="Bezeichnung der Position…" data-testid="position-title-input"
              onChange={(e) => setPosition({ ...position, title: e.target.value })}
              onBlur={(e) => updatePosition({ title: e.target.value })}
              className="mt-2 text-lg font-semibold border-0 px-0 focus-visible:ring-0 shadow-none" />
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input value={position.room || ""} placeholder="Raum" data-testid="position-room-input"
                onChange={(e) => setPosition({ ...position, room: e.target.value })}
                onBlur={(e) => updatePosition({ room: e.target.value })} className="h-8 text-sm" />
              <Input value={position.component || ""} placeholder="Bauteil" data-testid="position-component-input"
                onChange={(e) => setPosition({ ...position, component: e.target.value })}
                onBlur={(e) => updatePosition({ component: e.target.value })} className="h-8 text-sm" />
            </div>
            <Textarea value={position.description || ""} placeholder="Beschreibung der Position…" data-testid="position-description-input"
              onChange={(e) => setPosition({ ...position, description: e.target.value })}
              onBlur={(e) => updatePosition({ description: e.target.value })} rows={2} className="mt-2 text-sm" />
            <BadgeScaleControl position={position} setPosition={setPosition} updatePosition={updatePosition} />
            <div className="text-xs text-slate-400 mt-1">Ersteller: {position.created_by || "—"} · {(position.created_at || "").slice(0, 10)}</div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-5 mt-4 grid grid-cols-6">
            <TabsTrigger value="tickets" data-testid="tab-tickets" className="text-xs px-1"><Ticket className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Tickets</span></TabsTrigger>
            <TabsTrigger value="maengel" data-testid="tab-maengel" className="text-xs px-1"><AlertTriangle className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Mängel</span></TabsTrigger>
            <TabsTrigger value="hinweise" data-testid="tab-hinweise" className="text-xs px-1"><Info className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Hinweise</span></TabsTrigger>
            <TabsTrigger value="mengen" data-testid="tab-mengen" className="text-xs px-1"><Ruler className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Mengen</span></TabsTrigger>
            <TabsTrigger value="fotos" data-testid="tab-fotos" className="text-xs px-1"><ImageIcon className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Fotos</span></TabsTrigger>
            <TabsTrigger value="kommentare" data-testid="tab-kommentare" className="text-xs px-1"><MessageSquare className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Verlauf</span></TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto scroll-thin p-5">
            <TabsContent value="tickets" className="mt-0"><TicketsTab positionId={positionId} tickets={tickets} onChange={notify} /></TabsContent>
            <TabsContent value="maengel" className="mt-0"><DefectsTab positionId={positionId} defects={defects} onChange={notify} /></TabsContent>
            <TabsContent value="hinweise" className="mt-0"><PositionNotesTab positionId={positionId} onChange={notify} /></TabsContent>
            <TabsContent value="mengen" className="mt-0"><QuantitiesTab positionId={positionId} quantities={quantities} onChange={notify} /></TabsContent>
            <TabsContent value="fotos" className="mt-0"><FilesTab positionId={positionId} photos={photos} docs={docs} onChange={notify} /></TabsContent>
            <TabsContent value="kommentare" className="mt-0"><CommentsTab positionId={positionId} comments={comments} onChange={notify} /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function AssigneeSelect({ positionId, projectId, value, currentName, onChange, testid }) {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get("/tasks/assignees", { params: positionId ? { position_id: positionId } : { project_id: projectId } }).then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, [positionId, projectId]);
  const NONE = "__none";
  return (
    <div><Label className="text-xs">Zuständig (Meine Aufgaben)</Label>
      <Select value={value || NONE} onValueChange={(v) => { if (v) { onChange(v === NONE ? "" : v); } }}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— nicht zugewiesen —</SelectItem>
          {value && !members.some((m) => m.user_id === value) && <SelectItem value={value}>{currentName || "Bisher zuständig"}</SelectItem>}
          {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

async function saveAssignee(kind, id, before, after) {
  if ((before || "") === (after || "")) return;
  try { await api.patch(`/tasks/${kind}/${id}`, { assignee_id: after || "" }); }
  catch (e) { toast.error(`Zuständigkeit nicht gespeichert: ${formatApiError(e.response?.data?.detail)}`); }
}

export function TicketForm({ positionId, projectId, ticket, onSaved, onCancel, resolvePosition }) {
  const [form, setForm] = useState(ticket ? { ...EMPTY_TICKET, ...ticket } : EMPTY_TICKET);
  const { employees } = useStammdaten();
  const [busy, setBusy] = useState(false);
  const isEdit = !!ticket;
  const on = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const save = async () => {
    if (!form.title) return toast.error("Titel erforderlich");
    setBusy(true);
    try {
      if (isEdit) { await api.put(`/tickets/${ticket.id}`, { ...ticket, ...form }); await saveAssignee("ticket", ticket.id, ticket.assignee_id, form.assignee_id); toast.success("Ticket aktualisiert"); }
      else {
        const pid = resolvePosition ? await resolvePosition(form.title) : positionId;
        let data;
        try { ({ data } = await api.post(pid ? `/positions/${pid}/tickets` : `/projects/${projectId}/tickets`, form)); }
        catch (err) { if (resolvePosition && pid) { await api.delete(`/positions/${pid}`).catch(() => {}); } throw err; } if (data?.id) await saveAssignee("ticket", data.id, "", form.assignee_id); toast.success("Ticket erstellt"); onSaved(data); return; }
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50" data-testid={isEdit ? `ticket-edit-form-${ticket.id}` : "ticket-create-form"}>
      {isEdit && <div className="text-xs font-mono-tech font-bold text-slate-500">{ticket.ticket_number ? `${ticket.ticket_number} ` : ""}bearbeiten</div>}
      <Input placeholder="Titel *" value={form.title} onChange={on("title")} data-testid="ticket-title-input" />
      <div className="grid grid-cols-2 gap-2">
        <Select value={form.category} onValueChange={on("category")}><SelectTrigger data-testid="ticket-type-select"><SelectValue /></SelectTrigger><SelectContent>{TICKET_CATEGORIES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
        <Select value={form.priority} onValueChange={on("priority")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
      </div>
      <Textarea placeholder="Beschreibung des Mangels…" value={form.description} onChange={on("description")} rows={2} data-testid="ticket-desc-input" />
      <Textarea placeholder="Maßnahmen…" value={form.measures} onChange={on("measures")} rows={2} />
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Verantwortlich</Label><Input list="dl-employees" value={form.responsible} onChange={on("responsible")} data-testid="ticket-responsible-input" /><datalist id="dl-employees">{employees.map((e) => <option key={e.id} value={e.name} />)}</datalist></div>
        <div><Label className="text-xs">Frist</Label><Input type="date" value={form.due_date} onChange={on("due_date")} data-testid="ticket-due-input" /></div>
      </div>
      <AssigneeSelect positionId={positionId} projectId={projectId || ticket?.project_id} value={form.assignee_id} currentName={ticket?.assignee_name} onChange={(v) => setForm({ ...form, assignee_id: v })} testid="ticket-assignee-select" />
      {isEdit && <div><Label className="text-xs">Status</Label><Select value={form.status} onValueChange={on("status")}><SelectTrigger data-testid="ticket-edit-status-select"><SelectValue /></SelectTrigger><SelectContent>{TICKET_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>}
      <div className="flex gap-2"><Button onClick={save} disabled={busy} className="flex-1" data-testid="save-ticket-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Änderungen speichern" : "Speichern")}</Button><Button variant="ghost" onClick={onCancel}>Abbrechen</Button></div>
    </div>
  );
}

function TicketCard({ t, onChangeStatus, onDelete, onChange }) {
  const [editing, setEditing] = useState(false);
  const st = STATUS[t.status] || STATUS.offen;
  const typeLabel = TICKET_CATEGORIES.find((x) => x.value === (t.category || t.type))?.label;
  if (editing) {
    return <TicketForm positionId={t.position_id} ticket={t} onSaved={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />;
  }
  return (
    <div className={`border rounded-xl p-4 ${t.__pending ? (t.__failed ? "border-red-300 bg-red-50/40" : "border-amber-300 bg-amber-50/40") : "border-slate-200"}`} data-testid={`ticket-item-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {t.ticket_number && <span className="font-mono-tech text-[10px] text-slate-400">{t.ticket_number}</span>}
            <Badge variant="outline" className="text-[10px]">{typeLabel}</Badge>
            {(t.priority === "hoch" || t.priority === "kritisch") && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">{t.priority === "kritisch" ? "Kritisch" : "Hoch"}</Badge>}
            <span className="font-semibold text-slate-900">{t.title}</span>
            <PendingItemBadge item={t} />
          </div>
          {t.description && <p className="text-sm text-slate-600 mt-1">{t.description}</p>}
          {t.measures && <p className="text-xs text-slate-500 mt-1"><b>Maßnahmen:</b> {t.measures}</p>}
          <div className="flex items-center gap-3 text-xs text-slate-400 mt-2 flex-wrap">
            {t.assignee_name && <span className="text-primary font-medium" data-testid={`ticket-assignee-${t.id}`}>Zuständig: {t.assignee_name}</span>}
            {t.responsible && <span>👤 {t.responsible}</span>}
            {t.due_date && <span>📅 {t.due_date}</span>}
            {t.created_by && <span>von {t.created_by}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-primary" data-testid={`edit-ticket-${t.id}`} title="Ticket bearbeiten"><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(t.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-ticket-${t.id}`} title="Ticket löschen"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-3">
        <Select value={t.status} onValueChange={(v) => onChangeStatus(t, v)}>
          <SelectTrigger className="h-8 w-44" data-testid={`ticket-status-${t.id}`}><span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: st.dot }} /><SelectValue /></span></SelectTrigger>
          <SelectContent>{TICKET_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

function TicketsTab({ positionId, tickets, onChange }) {
  const [adding, setAdding] = useState(false);
  const changeStatus = async (t, status) => { await api.put(`/tickets/${t.id}`, { ...t, status }); onChange(); };
  const del = async (id) => { await api.delete(`/tickets/${id}`); onChange(); };
  const { user } = useAuth();

  return (
    <div className="space-y-3">
      {!adding && can(user, "ticket:write") && <Button onClick={() => setAdding(true)} variant="outline" className="w-full" data-testid="add-ticket-btn"><Plus className="h-4 w-4 mr-2" />Ticket / Mangel / Aufgabe</Button>}
      {adding && <TicketForm positionId={positionId} onSaved={() => { setAdding(false); onChange(); }} onCancel={() => setAdding(false)} />}
      {tickets.length === 0 && !adding && <p className="text-sm text-slate-400 text-center py-6">Noch keine Tickets erfasst.</p>}
      {tickets.map((t) => <TicketCard key={t.id} t={t} onChangeStatus={changeStatus} onDelete={del} onChange={onChange} />)}
    </div>
  );
}

function QuantitiesTab({ positionId, quantities, onChange }) {
  const [form, setForm] = useState(EMPTY_QTY);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.service || form.amount === "") return toast.error("Leistung und Menge erforderlich");
    setBusy(true);
    try { await api.post(`/positions/${positionId}/quantities`, { ...form, amount: parseFloat(form.amount) || 0, position_id: positionId }); setForm({ ...EMPTY_QTY, date: form.date, company: form.company }); onChange(); toast.success("Mengenposition hinzugefügt"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sums = quantities.reduce((acc, q) => { acc[q.unit] = (acc[q.unit] || 0) + (q.amount || 0); return acc; }, {});
  return (
    <div className="space-y-3">
      <QuantityForm form={form} setForm={setForm} positions={[]} onSave={save} busy={busy} hidePosition />
      {quantities.map((q) => <QuantityRow key={q.id} q={q} onChanged={onChange} showPosition={false} />)}
      {quantities.length > 0 && <div className="flex justify-between items-center pt-2 border-t border-slate-200 font-semibold text-sm"><span>Summen</span><span className="font-mono-tech text-primary">{Object.entries(sums).map(([u, v]) => `${Math.round(v * 100) / 100} ${u}`).join(" · ")}</span></div>}
    </div>
  );
}

function FilesTab({ positionId, photos, docs, onChange }) {
  return (
    <div className="space-y-5">
      <MediaUploader links={{ position_id: positionId }} onUploaded={onChange} />
      {photos.length > 0 && <div><h4 className="text-sm font-semibold text-slate-700 mb-2">Fotos ({photos.length})</h4><MediaGrid photos={photos} onChanged={onChange} /></div>}
      {docs.length > 0 && (
        <div><h4 className="text-sm font-semibold text-slate-700 mb-2">Dokumente ({docs.length})</h4>
          <div className="space-y-2">
            {docs.map((f) => (
              <div key={f.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5" data-testid={`doc-${f.id}`}>
                <FileText className="h-4 w-4 text-sky-600" />
                <span className="flex-1 text-sm truncate">{f.original_filename}</span>
                <button onClick={() => downloadFile(f)} className="text-slate-400 hover:text-primary"><Download className="h-4 w-4" /></button>
                <button onClick={async () => { await api.delete(`/files/${f.id}`); onChange(); }} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {photos.length === 0 && docs.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Noch keine Fotos oder Dokumente.</p>}
    </div>
  );
}

function DefectPhotos({ defectId, onChange, readOnly = false, open = false }) {
  const [photos, setPhotos] = useState([]);
  const load = useCallback(() => api.get(`/defects/${defectId}/photos`).then((r) => setPhotos(r.data)), [defectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("offline-queued", load);
    return () => window.removeEventListener("offline-queued", load);
  }, [load]);
  const refresh = () => { load(); onChange && onChange(); };
  const before = photos.filter((p) => p.phase === "vorher"), after = photos.filter((p) => p.phase === "nachher"), rest = photos.filter((p) => !p.phase);
  return (
    <div className="mt-3 space-y-2" data-testid={`defect-photos-${defectId}`}>
      {!readOnly && <MediaUploader links={{ defect_id: defectId }} onUploaded={refresh} allowPhase compact defaultPhase={open && after.length === 0 ? "vorher" : ""} />}
      {(before.length > 0 || after.length > 0) && (
        <div className="grid grid-cols-2 gap-2">
          <div><div className="text-[10px] font-bold text-red-600 uppercase mb-1">Vorher ({before.length})</div><MediaGrid photos={before} onChanged={refresh} allowPhase cols="grid-cols-2" /></div>
          <div><div className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Nachher ({after.length})</div><MediaGrid photos={after} onChanged={refresh} allowPhase cols="grid-cols-2" /></div>
        </div>
      )}
      {rest.length > 0 && <MediaGrid photos={rest} onChanged={refresh} allowPhase cols="grid-cols-4" />}
    </div>
  );
}

function DefectForm({ positionId, defect, onSaved, onCancel }) {
  const [form, setForm] = useState(defect ? { ...EMPTY_DEFECT, ...defect } : EMPTY_DEFECT);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef();
  const { companies, categories } = useStammdaten();
  const on = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const isEdit = !!defect;

  const aiText = async () => {
    if (!form.bezeichnung && !form.description) return toast.error("Bitte Bezeichnung oder Stichworte eingeben");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/defect-text", { bezeichnung: form.bezeichnung, description: form.description, gewerk: form.gewerk });
      setForm((p) => ({ ...p, bezeichnung: p.bezeichnung || data.bezeichnung || "", description: data.description || p.description,
        gewerk: p.gewerk || data.gewerk || "", priority: data.priority || p.priority,
        cause: p.cause || data.cause || "", proposed_measure: p.proposed_measure || data.proposed_measure || "" }));
      toast.success("KI-Vorschlag übernommen – bitte prüfen");
    } catch { toast.error("KI nicht verfügbar"); } finally { setAiBusy(false); }
  };
  const aiPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ai/analyze-photo", fd);
      setForm((p) => ({ ...p, bezeichnung: p.bezeichnung || data.bezeichnung || "", description: data.description || p.description,
        gewerk: p.gewerk || data.gewerk || "", priority: data.priority || p.priority }));
      toast.success("Foto analysiert – Vorschlag bitte prüfen");
    } catch { toast.error("Bildanalyse fehlgeschlagen"); } finally { setAiBusy(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.bezeichnung) return toast.error("Mangelbezeichnung erforderlich");
    setBusy(true);
    try {
      if (isEdit) { await api.put(`/defects/${defect.id}`, { ...defect, ...form }); await saveAssignee("defect", defect.id, defect.assignee_id, form.assignee_id); toast.success("Mangel aktualisiert"); }
      else { const { data } = await api.post(`/positions/${positionId}/defects`, form); if (data?.id) await saveAssignee("defect", data.id, "", form.assignee_id); toast.success("Mangel erfasst"); }
      onSaved();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-red-200 rounded-xl p-4 space-y-3 bg-red-50/40" data-testid={isEdit ? `defect-edit-form-${defect.id}` : "defect-create-form"}>
      {isEdit && <div className="text-xs font-mono-tech font-bold text-red-600">Mangel {defect.number} bearbeiten</div>}
      <Input placeholder="Mangelbezeichnung *" value={form.bezeichnung} onChange={on("bezeichnung")} data-testid="defect-bezeichnung-input" />
      <Textarea placeholder="Beschreibung des Mangels…" value={form.description} onChange={on("description")} rows={2} data-testid="defect-desc-input" />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={aiText} disabled={aiBusy} className="flex-1 text-xs" data-testid="ai-defect-text-btn">{aiBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1 text-amber-500" />}KI-Text erstellen</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={aiBusy} className="flex-1 text-xs" data-testid="ai-defect-photo-btn"><Camera className="h-3.5 w-3.5 mr-1 text-sky-500" />Foto analysieren</Button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={aiPhoto} data-testid="ai-defect-photo-input" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Gewerk</Label>
          <Select value={form.gewerk} onValueChange={on("gewerk")}><SelectTrigger data-testid="defect-gewerk-select"><SelectValue placeholder="Wählen…" /></SelectTrigger><SelectContent>{[...new Set([...GEWERKE, ...categories.map((c) => c.name)])].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">Ort</Label><Input value={form.location} onChange={on("location")} data-testid="defect-location-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Verantwortliche Firma</Label><Input list="dl-companies" value={form.company} onChange={on("company")} data-testid="defect-company-input" /><datalist id="dl-companies">{companies.map((c) => <option key={c.id} value={c.name} />)}</datalist></div>
        <div><Label className="text-xs">Verantw. Mitarbeiter</Label><Input value={form.employee} onChange={on("employee")} /></div>
      </div>
      <AssigneeSelect positionId={positionId} value={form.assignee_id} currentName={defect?.assignee_name} onChange={(v) => setForm({ ...form, assignee_id: v })} testid="defect-assignee-select" />
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">Priorität</Label><Select value={form.priority} onValueChange={on("priority")}><SelectTrigger data-testid="defect-priority-select"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">Frist</Label><Input type="date" value={form.due_date} onChange={on("due_date")} data-testid="defect-due-input" /></div>
        <div><Label className="text-xs">Status</Label><Select value={form.status} onValueChange={on("status")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DEFECT_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <Textarea placeholder="Ursache…" value={form.cause} onChange={on("cause")} rows={2} />
      <Textarea placeholder="Vorgeschlagene Maßnahme…" value={form.proposed_measure} onChange={on("proposed_measure")} rows={2} />
      <Textarea placeholder="Durchgeführte Maßnahme…" value={form.actual_measure} onChange={on("actual_measure")} rows={2} data-testid="defect-actual-measure-input" />
      <div className="flex gap-2"><Button onClick={save} disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="save-defect-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Änderungen speichern" : "Mangel speichern")}</Button><Button variant="ghost" onClick={onCancel}>Abbrechen</Button></div>
    </div>
  );
}

function DeletionRequest({ d, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setOpen(false); onChange(); window.dispatchEvent(new Event("pd-deletion-requests-changed")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const req = d.deletion_request;
  if (req) {
    return (
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid={`deletion-request-${d.id}`}>
        <div><b>Löschung angefragt</b> von {req.requested_by_name} am {fmtDateTime(req.requested_at)}{req.note ? ` – „${req.note}“` : ""}</div>
        {can(user, "defect:approve") && (
          <div className="flex gap-2 mt-2">
            <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700" disabled={busy} data-testid={`approve-deletion-${d.id}`}
              onClick={() => run(() => api.post(`/defects/${d.id}/deletion-request/decide`, { approve: true }), "Mangel als erledigt archiviert")}>Bestätigen & archivieren</Button>
            <Button size="sm" variant="outline" className="h-7" disabled={busy} data-testid={`reject-deletion-${d.id}`}
              onClick={() => run(() => api.post(`/defects/${d.id}/deletion-request/decide`, { approve: false }), "Löschanfrage abgelehnt")}>Ablehnen</Button>
          </div>
        )}
      </div>
    );
  }
  if (can(user, "defect:delete") || !can(user, "defect:write")) return null;
  return (
    <div className="mt-3">
      {!open ? (
        <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" onClick={() => setOpen(true)} data-testid={`request-deletion-${d.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" />Löschung anfragen</Button>
      ) : (
        <div className="rounded-lg border border-slate-200 p-2.5 space-y-2" data-testid={`request-deletion-form-${d.id}`}>
          <p className="text-xs text-slate-500">Voraussetzung: ein „Nachher“-Foto der Behebung ist hochgeladen. Administrator oder Leitung bestätigen die Anfrage.</p>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hinweis (optional), z. B. was erledigt wurde" className="h-8 text-sm" data-testid={`request-deletion-note-${d.id}`} />
          <div className="flex gap-2">
            <Button size="sm" className="h-8" disabled={busy} onClick={() => run(() => api.post(`/defects/${d.id}/deletion-request`, { note }), "Löschung angefragt")} data-testid={`request-deletion-send-${d.id}`}>Anfrage senden</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen(false)}>Abbrechen</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "");

function ArchivedDefects({ defects, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!defects.length) return null;
  const restore = async (id) => {
    try { await api.post(`/defects/${id}/restore`); toast.success("Mangel wiederhergestellt"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-green-200 bg-green-50/40" data-testid="archived-defects">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-green-800" data-testid="archived-defects-toggle">
        <span>Erledigte Mängel ({defects.length})</span><span className="text-xs font-normal text-green-700">{open ? "ausblenden" : "anzeigen"}</span>
      </button>
      {open && (
        <div className="p-2 space-y-2">
          {defects.map((d) => (
            <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`archived-defect-${d.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-tech text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">{d.number}</span>
                <span className="font-semibold text-slate-800">{d.bezeichnung}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Archiviert am {fmtDateTime(d.archived_at)} von {d.archived_by} · wird am {fmtDateTime(d.purge_after).split(",")[0]} automatisch gelöscht</div>
              <DefectPhotos defectId={d.id} onChange={onChange} readOnly />
              {can(user, "defect:approve") && <Button size="sm" variant="outline" className="h-7 mt-2" onClick={() => restore(d.id)} data-testid={`restore-defect-${d.id}`}>Wiederherstellen</Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DefectCard({ d, onChangeStatus, onDelete, onChange }) {
  const [editing, setEditing] = useState(false);
  const { user } = useAuth();
  const st = STATUS[d.status] || STATUS.offen;
  if (editing) {
    return <DefectForm positionId={d.position_id} defect={d} onSaved={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />;
  }
  return (
    <div className={`border rounded-xl p-4 ${d.__pending ? (d.__failed ? "border-red-300 bg-red-50/40" : "border-amber-300 bg-amber-50/40") : "border-slate-200"}`} data-testid={`defect-item-${d.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-tech text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{d.number}</span>
            {(d.priority === "hoch" || d.priority === "kritisch") && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">{d.priority === "kritisch" ? "Kritisch" : "Hoch"}</Badge>}
            <span className="font-semibold text-slate-900">{d.bezeichnung}</span>
            <PendingItemBadge item={d} />
            <PendingPhotoBadge defectId={d.id} />
          </div>
          {d.description && <p className="text-sm text-slate-600 mt-1">{d.description}</p>}
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-2">
            {d.gewerk && <span>Gewerk: {d.gewerk}</span>}
            {d.location && <span>Ort: {d.location}</span>}
            {d.company && <span>Firma: {d.company}</span>}
            {d.assignee_name && <span className="text-primary font-medium" data-testid={`defect-assignee-${d.id}`}>Zuständig: {d.assignee_name}</span>}
            {d.employee && <span>Mitarbeiter: {d.employee}</span>}
            {d.due_date && <span>Frist: {d.due_date}</span>}
          </div>
          {d.cause && <p className="text-xs text-slate-500 mt-1"><b>Ursache:</b> {d.cause}</p>}
          {d.proposed_measure && <p className="text-xs text-slate-500 mt-0.5"><b>Vorgeschlagen:</b> {d.proposed_measure}</p>}
          {d.actual_measure && <p className="text-xs text-slate-500 mt-0.5"><b>Durchgeführt:</b> {d.actual_measure}</p>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-primary" data-testid={`edit-defect-${d.id}`} title="Mangel bearbeiten"><Pencil className="h-4 w-4" /></button>
          {can(user, "defect:delete") && <button onClick={() => onDelete(d.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-defect-${d.id}`} title="Mangel löschen"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      <DefectPhotos defectId={d.id} onChange={onChange} open={!d.deletion_request && !["erledigt", "abgenommen", "geschlossen", "behoben"].includes(d.status)} />
      <div className="mt-3">
        <Select value={d.status} onValueChange={(v) => onChangeStatus(d, v)}>
          <SelectTrigger className="h-8 w-44" data-testid={`defect-status-${d.id}`}><span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: st.dot }} /><SelectValue /></span></SelectTrigger>
          <SelectContent>{DEFECT_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <DeletionRequest d={d} onChange={onChange} />
    </div>
  );
}

function DefectsTab({ positionId, defects, onChange }) {
  const [adding, setAdding] = useState(false);
  const changeStatus = async (d, status) => { await api.put(`/defects/${d.id}`, { ...d, status }); onChange(); };
  const del = async (id) => { await api.delete(`/defects/${id}`); onChange(); };
  const { user } = useAuth();

  return (
    <div className="space-y-3">
      {!adding && can(user, "defect:write") && <Button onClick={() => setAdding(true)} variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50" data-testid="add-defect-btn"><AlertTriangle className="h-4 w-4 mr-2" />Mangel dokumentieren</Button>}
      {adding && <DefectForm positionId={positionId} onSaved={() => { setAdding(false); onChange(); }} onCancel={() => setAdding(false)} />}
      {defects.filter((d) => !d.archived).length === 0 && !adding && <p className="text-sm text-slate-400 text-center py-6">Keine offenen Mängel dokumentiert.</p>}
      {defects.filter((d) => !d.archived).map((d) => <DefectCard key={d.id} d={d} onChangeStatus={changeStatus} onDelete={del} onChange={onChange} />)}
      <ArchivedDefects defects={defects.filter((d) => d.archived)} onChange={onChange} />
    </div>
  );
}

const AUDIT_ACTIONS = {
  created: "erstellt", updated: "geändert", deleted: "gelöscht", status_changed: "Status geändert",
  comment_added: "Kommentar hinzugefügt", photo_added: "Foto hinzugefügt",
};
function auditLabel(a) {
  if (a.action === "status_changed") return `${a.entity_label}: ${a.old_value || "—"} → ${a.new_value || "—"}`;
  if (a.action === "comment_added") return "Kommentar hinzugefügt";
  if (a.action === "photo_added") return "Foto hinzugefügt";
  if (a.action === "updated" && a.field) return `${a.entity_label || ""} geändert: ${a.field}`.trim();
  return `${a.entity_label || ""} ${AUDIT_ACTIONS[a.action] || a.action}`.trim();
}

function CommentsTab({ positionId, comments, onChange }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);
  useEffect(() => { api.get(`/audit?position_id=${positionId}`).then((r) => setAudit(r.data)).catch(() => setAudit([])); }, [positionId, comments]);
  const save = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/positions/${positionId}/comments`, { text }); setText(""); onChange(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <form onSubmit={save} className="flex gap-2">
        <Input placeholder="Kommentar / Notiz…" value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input" />
        <Button type="submit" disabled={busy} data-testid="save-comment-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
      </form>
      <div className="space-y-3">
        {comments.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Noch keine Kommentare.</p>}
        {comments.map((c) => (
          <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
            <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">{(c.author || "?").slice(0, 2).toUpperCase()}</div>
            <div className="flex-1"><div className="bg-slate-100 rounded-xl px-3 py-2 text-sm text-slate-800">{c.text}</div><div className="text-xs text-slate-400 mt-1">{c.author} · {(c.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
          </div>
        ))}
      </div>
      {audit.length > 0 && (
        <div className="pt-3 border-t border-slate-200" data-testid="audit-log">
          <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" />Änderungsprotokoll</div>
          <div className="space-y-1">
            {audit.map((a) => (
              <div key={a.id} className="text-xs text-slate-500 flex gap-2 items-start" data-testid={`audit-${a.id}`}>
                <span className="text-slate-300 mt-0.5">•</span>
                <span><span className="text-slate-700">{auditLabel(a)}</span> · <b>{a.actor}</b> · {(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
